<template>
  <b-modal
    :id="modalId"
    ref="bulkImportModal"
    :visible="localVisible"
    size="lg"
    title="Importação em Massa"
    hide-footer
    @hide="onHide"
  >
    <div>
      <b-progress
        :max="totalSteps"
        height="8px"
        class="mb-3"
      >
        <b-progress-bar
          :value="currentStep"
          :max="totalSteps"
          variant="info"
        />
      </b-progress>

      <div v-if="selectionNeeded && currentStep === 1">
        <h5>1. Selecionar tipo de entidade</h5>
        <b-form-group description="Selecione o tipo de entidade para importação">
          <b-form-select
            v-model="selectedTipoEntidade"
            :options="tipoEntidadeSelectOptions"
            placeholder="Escolha o tipo de entidade..."
          />
        </b-form-group>

        <b-row class="mt-3">
          <b-col>
            <b-button
              variant="secondary"
              @click="close"
            >
              Cancelar
            </b-button>
          </b-col>
          <b-col cols="auto">
            <b-button
              :disabled="!selectedTipoEntidade"
              variant="primary"
              @click="onSelectNext"
            >
              Próximo
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div v-else-if="currentStep === (selectionNeeded ? 2 : 1)">
        <!-- Mostra os campos esperados (label, descrição, tipo traduzido) -->
        <div
          v-if="expectedFormatToShow && expectedFormatToShow.length"
          class="mb-2"
        >
          <div class="d-flex justify-content-between align-items-center mb-1">
            <small class="text-muted">{{ expectedFormatToShow.length }} campos esperados</small>
            <div>
              <b-button
                size="sm"
                variant="link"
                @click="expectedExpanded = !expectedExpanded"
              >
                {{ expectedExpanded ? 'Ocultar' : 'Mostrar todos' }}
              </b-button>
            </div>
          </div>

          <div :class="['expected-format-scroll', { expanded: expectedExpanded }]">
            <ul class="mb-0 pl-3">
              <li
                v-for="(field, index) in expectedFormatToShow"
                :id="`expectedFormat-${index}`"
                :key="index"
              >
                <div>
                  <strong>{{ field.label || field.name }} </strong>
                  <span
                    v-if="field.required"
                    class="text-danger"
                    title="Obrigatório"
                  >*</span>
                </div>
                <div class="small text-muted">
                  {{ field.description || '— sem descrição —' }}
                  <span class="ml-2">• <em>{{ field.typeLabel || translateType(field.type) }}</em></span>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <h5>Enviar arquivo (Planilha ou CSV)</h5>
        <b-form-group description="Aceito: xls, xlsx, csv">
          <b-form-file
            v-model="file"
            :state="fileState"
            :accept="acceptString"
            browse-text="Escolher"
            placeholder="Selecione um arquivo..."
            @input="onFileSelected"
          />
        </b-form-group>

        <div v-if="file">
          <p class="mb-1">
            <strong>Arquivo:</strong>
            {{ file.name }}
          </p>
          <p class="mb-1">
            <strong>Tamanho:</strong>
            {{ humanFileSize(file.size) }}
          </p>
        </div>

        <b-alert
          v-if="error"
          variant="danger"
          show
        >
          {{ error }}
        </b-alert>

        <b-row class="mt-3">
          <b-col>
            <b-button
              variant="secondary"
              @click="close"
            >
              Cancelar
            </b-button>
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="primary"
              :disabled="!file || uploading"
              @click="uploadFile"
            >
              <span v-if="uploading">
                Enviando... {{ uploadProgress }}%
              </span>
              <span v-else>
                Enviar
              </span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Mapeamento de campos -->
      <div v-else-if="currentStep === (selectionNeeded ? 3 : 2)">
        <h5>2. Mapear campos</h5>

        <p v-if="!serverHeaders.length">
          Nenhum cabeçalho encontrado no arquivo.
        </p>

        <b-table
          small
          :items="resourceFields"
          :fields="mappingTableFields"
        >
          <template #cell(label)="row">
            <div>
              <span>{{ row.item.label || row.item.name }}</span>
              <span
                v-if="row.item.required"
                class="text-danger"
                title="Obrigatório"
              >*</span>
              <span
                v-if="mappings[row.item.name]"
                class="text-success ml-1"
                :title="mappingAuto[row.item.name] ? 'Auto mapeado' : 'Mapeado'"
              >✓</span>
            </div>
          </template>

          <template #cell(headerSelect)="row">
            <b-form-select
              v-model="mappings[row.item.name]"
              :options="headerOptionsFor(row.item)"
              class="w-100"
              :state="mappingErrors[row.item.name] ? false : null"
              @change="onMappingChange(row.item, $event)"
            />
          </template>
        </b-table>

        <b-row class="mt-3">
          <b-col>
            <b-button
              variant="secondary"
              @click="prevStep"
            >
              Voltar
            </b-button>
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="primary"
              @click="confirmMappings"
            >
              Confirmar e Importar
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div v-else-if="currentStep === (selectionNeeded ? 4 : 3)">
        <h5>3. Confirmar importação</h5>
        <p>
          Confirme os detalhes abaixo e clique em "Importar Registros" para iniciar a importação.
        </p>

        <b-list-group>
          <b-list-group-item>
            <strong>Arquivo:</strong>
            {{ uploadedFileName }}
          </b-list-group-item>
          <b-list-group-item>
            <strong>Linhas estimadas:</strong>
            {{ serverRowCount || 'Não informado' }}
          </b-list-group-item>
          <b-list-group-item>
            <strong>Mapeamentos:</strong>
            <ul class="mb-0">
              <li
                v-for="f in resourceFields"
                :key="f.name"
              >
                {{ f.label || f.name }}:
                <em>
                  {{ mappings[f.name] || '— ignorado —' }}
                </em>
              </li>
            </ul>
          </b-list-group-item>
        </b-list-group>

        <b-row class="mt-3">
          <b-col>
            <b-button
              variant="secondary"
              @click="prevStep"
            >
              Voltar
            </b-button>
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="success"
              :disabled="importing"
              @click="importRecords"
            >
              <span v-if="importing">Importando...</span>
              <span v-else>Importar Registros</span>
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>
  </b-modal>
</template>

<script>
import UploadMixin from '../../../../shared/mixins/upload/Upload.vue'
import api from './services/api'

export default {
  name: 'BulkRecordImportModal',
  mixins: [UploadMixin],
  props: {
    value: { type: Boolean, default: false },
    modalId: { type: String, default: 'bulk-import-modal' },

    tipoEntidade: { type: String, default: null },
    allowedExtensions: { type: String, default: 'xls;xlsx;csv' },
    maxSize: { type: Number, default: 0 }, // bytes, 0 = unlimited
    // If caller wants the modal to let user choose entity types, pass
    // `tipoEntidadeOptions` as [{ value: 'Foo', text: 'Foo' }, ...]
    tipoEntidadeOptions: { type: Array, default: () => [] },
    // Controls whether the modal shows the success alert after import.
    // If false, the modal will still emit "imported" and let the parent
    // decide how to notify the user.
    notifyOnImport: { type: Boolean, default: true },
    // Optional help text or array of expected columns/format to show on step 1
    expectedFormat: { type: [String, Array], default: null },
  },
  data() {
    return {
      localVisible: this.value,
      currentStep: 1,
      // Local selected tipoEntidade. Initialized from prop if present.
      selectedTipoEntidade: this.tipoEntidade || null,
      file: null,
      fileState: null,
      error: '',
      uploading: false,
      uploadProgress: 0,
      serverHeaders: [],
      resourceFields: [],
      mappings: {},
      mappingErrors: {},
      mappingAuto: {},
      importacaoId: null,
      uploadedFileName: null,
      serverRowCount: null,
      importing: false,
      // controla se a lista de expected fields está expandida ou não
      expectedExpanded: false,
      // Local copy of expected format coming from prop or API. Do not mutate prop directly.
      expectedFormatToShow: Array.isArray(this.expectedFormat) ? this.expectedFormat.map(f => ({
        name: f.name,
        required: f.required,
        label: f.label,
        description: f.description,
        type: f.type,
        typeLabel: this.translateType ? this.translateType(f.type) : (f.type || ''),
      })) : [],
      // local cache for options fetched from API when the parent doesn't pass them
      tipoEntidadeOptionsLocal: [],
    }
  },
  computed: {
    acceptString() {
      // Build accept string for input
      return this.allowedExtensions.split(';').map(e => `.${e.trim()}`).join(',')
    },
    mappingTableFields() {
      return [
        { key: 'label', label: 'Campo do Recurso' },
        { key: 'headerSelect', label: 'Cabeçalho correspondente' },
      ]
    },
    // Whether the modal should show the initial selection step
    selectionNeeded() {
      // If parent provided a tipoEntidade prop we don't ask user to select it
      return !this.tipoEntidade
    },
    // total number of steps in the progress bar
    totalSteps() {
      return 3 + (this.selectionNeeded ? 1 : 0)
    },
    // build select options for tipoEntidade selection
    tipoEntidadeSelectOptions() {
      const source = (this.tipoEntidadeOptions && this.tipoEntidadeOptions.length) ? this.tipoEntidadeOptions : this.tipoEntidadeOptionsLocal
      if (!source || !source.length) return []
      return source.map(opt => {
        if (typeof opt === 'string') return { value: opt, text: opt }
        if (opt && opt.value !== undefined && opt.text !== undefined) return opt
        // support backend DTO { Key, Label } or other casing variants
        return { value: opt.Key || opt.key || opt.value, text: opt.Label || opt.label || opt.text }
      })
    },
  },
  watch: {
    value(v) { this.localVisible = v },
    localVisible(v) { this.$emit('input', v) },
    // keep local selection in sync if parent updates the prop
    tipoEntidade(val) { this.selectedTipoEntidade = val || null },
  },
  created() {
    // If the parent provided a tipoEntidade prop, fetch expected fields
    if (this.tipoEntidade) {
      api.getCamposEntidade(this.tipoEntidade).then(resp => {
        const data = (resp && resp.data) ? resp.data : []
        if (Array.isArray(data) && data.length) {
          this.expectedFormatToShow = data.map(f => ({
            name: f.name,
            label: f.label || f.name,
            required: f.required,
            description: f.description || '',
            type: f.type || 'string',
            typeLabel: this.translateType(f.type),
          }))
        }
      }).catch(() => {
        // ignore: keep expectedFormatToShow as-is (could be provided via prop)
      })
    }
    // If parent didn't pass explicit options, try to fetch them from the API
    if (!this.tipoEntidadeOptions || !this.tipoEntidadeOptions.length) {
      api.getTipoEntidadeOptions().then(resp => {
        const data = (resp && resp.data) ? resp.data : []
        // Normalize backend DTOs to { value, text } locally here so api.js stays
        // consistent with other methods (returns raw axios response).
        const normalized = (Array.isArray(data) ? data.map(item => {
          if (typeof item === 'string') return { value: item, text: item }
          if (item && item.value !== undefined && item.text !== undefined) return item
          // support TipoEntidadeDTO: { Key, Label } (any casing)
          return { value: item.Key || item.key || item.Value || item.value, text: item.Label || item.label || item.Text || item.text }
        }) : [])
        this.tipoEntidadeOptionsLocal = normalized
      }).catch(() => {
        // ignore failures; UI will still work if parent provides options
      })
    }
  },
  methods: {
    // Traduz tipos básicos para Português
    translateType(type) {
      if (!type) return 'Texto'
      const t = (type || '').toString().toLowerCase()
      if (['string', 'str', 'text', 'varchar', 'char'].includes(t)) return 'Texto'
      if (['number', 'numeric', 'double', 'float', 'decimal'].includes(t)) return 'Número'
      if (['int', 'integer', 'long', 'short'].includes(t)) return 'Inteiro'
      if (['boolean', 'bool'].includes(t)) return 'Booleano (sim) ou (não)'
      if (['date', 'datetime', 'timestamp'].includes(t)) return 'Data/Hora'
      if (['array', 'list'].includes(t)) return 'Lista'
      if (['object', 'json'].includes(t)) return 'Objeto'
      return type
    },
    close() {
      this.reset()
      this.localVisible = false
      this.$refs.bulkImportModal.hide()
    },
    showBulkImportModal() {
      // reset selection to prop value when opening
      this.selectedTipoEntidade = this.tipoEntidade || null
      this.currentStep = 1
      this.$refs.bulkImportModal.show()
    },
    // Called when user selects a tipoEntidade and clicks next on the selection step
    async onSelectNext() {
      if (!this.selectedTipoEntidade) {
        this.error = 'Selecione um tipo de entidade.'
        return
      }
      this.error = ''
      // fetch expected fields for the selected tipoEntidade
      try {
        const resp = await api.getCamposEntidade(this.selectedTipoEntidade)
        const data = (resp && resp.data) ? resp.data : []
        if (Array.isArray(data) && data.length) {
          this.expectedFormatToShow = data.map(f => ({
            name: f.name,
            label: f.label || f.name,
            required: f.required,
            description: f.description || '',
            type: f.type || 'string',
            typeLabel: this.translateType(f.type),
          }))
        }
      } catch (e) {
        // ignore
      }
      // advance to the upload step
      this.currentStep = 2
    },
    onHide() {
      this.$emit('closed')
      this.reset()
    },
    onFileSelected(file) {
      this.error = ''
      if (!file) {
        this.fileState = null
        return
      }

      // validate extension
      const okExt = this.allowedExtensions.split(';').some(ext => new RegExp(`\\.${ext.trim()}$`, 'i').test(file.name))
      if (!okExt) {
        this.error = 'Formato inválido. Utilize xls, xlsx ou csv.'
        this.fileState = false
        return
      }

      // validate size
      if (this.maxSize > 0 && this.invalidFileSize(this.maxSize, file.size)) {
        this.error = `Arquivo muito grande. Máx: ${this.humanFileSize(this.maxSize)}`
        this.fileState = false
        return
      }

      this.fileState = true
      this.uploadedFileName = file.name
    },
    async uploadFile() {
      if (!this.file) return

      // Ensure we have a tipoEntidade selected
      if (!this.selectedTipoEntidade) {
        this.error = 'Tipo de entidade não informado.'
        return
      }

      this.uploading = true
      this.uploadProgress = 0
      this.error = ''

      const form = new FormData()
      form.append('file', this.file)

      try {
        const resp = await api.uploadFile(this.selectedTipoEntidade, form, percent => { this.uploadProgress = percent })
        const data = resp.data || {}
        // Try to support multiple backend shapes
        this.serverHeaders = data.headers || data.columns || data.cabecalhos || []
        this.resourceFields = data.fields || data.resourceFields || data.campos || []
        this.importacaoId = data.importacaoId || data.id || null
        this.serverRowCount = data.rowCount || data.count || null

        // Normalize resourceFields to {name,label}
        this.resourceFields = this.resourceFields.map(f => (typeof f === 'string' ? { name: f, label: f } : f))

        // Auto-match: prefer the field label when available and normalize both
        // header and field strings by lowercasing and removing non-alphanumeric
        // characters so variations like `cadastro_federal` and `CadastroFederal`
        // will match.
        const normalize = s => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '')

        this.resourceFields.forEach(f => {
          const fieldKey = f.label || f.name || ''
          const normalizedField = normalize(fieldKey)
          const match = this.serverHeaders.find(h => normalize(h) === normalizedField)
          this.$set(this.mappings, f.name, match || null)
          // mark whether this field was auto-mapped
          this.$set(this.mappingAuto, f.name, !!match)
        })

        // advance to mapping step (account for optional selection step)
        this.currentStep = this.selectionNeeded ? 3 : 2
      } catch (err) {
        this.error = (err && err.response && err.response.data && err.response.data.message) || err.message || 'Erro ao enviar arquivo.'
      } finally {
        this.uploading = false
      }
    },
    headerOptionsFor() {
      const opts = [{ value: null, text: '— ignorar —' }]
      this.serverHeaders.forEach(h => opts.push({ value: h, text: h }))
      return opts
    },
    onMappingChange(field, value) {
      // clear any validation error for this field when user changes mapping
      if (value) {
        this.$delete(this.mappingErrors, field.name)
        this.error = ''
        // if user changed mapping, it's no longer considered an auto-mapping
        this.$set(this.mappingAuto, field.name, false)
      }
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1
    },
    confirmMappings() {
      // Validate required fields are mapped before moving to confirmation
      this.mappingErrors = {}
      const missing = this.resourceFields.filter(f => f.required && !this.mappings[f.name])
      if (missing && missing.length > 0) {
        missing.forEach(f => { this.$set(this.mappingErrors, f.name, true) })
        const names = missing.map(m => (m.label || m.name)).join(', ')
        this.error = `Os campos obrigatórios não mapeados: ${names}`
        // stay on mapping step
        this.currentStep = this.selectionNeeded ? 3 : 2
        return
      }
      this.error = ''
      this.currentStep = this.selectionNeeded ? 4 : 3
    },
    async importRecords() {
      // Defensive validation again before sending
      const missing = this.resourceFields.filter(f => f.required && !this.mappings[f.name])
      if (missing && missing.length > 0) {
        missing.forEach(f => { this.$set(this.mappingErrors, f.name, true) })
        const names = missing.map(m => (m.label || m.name)).join(', ')
        this.error = `Os campos obrigatórios não mapeados: ${names}`
        return
      }

      this.importing = true
      this.error = ''

      try {
        const payload = {
          importacaoId: this.importacaoId,
          mappings: this.mappings,
        }

        const resp = await api.confirmImport(payload)
        // notify parent that import was scheduled/started
        this.$emit('imported', resp.data)

        // close the bulk import modal before showing the global alert
        this.close()

        // Optionally show a SweetAlert informing the user the import will run
        // in background and offer to navigate to the imports page. If the
        // caller set `notifyOnImport=false`, the parent will handle notifying.
        if (this.notifyOnImport) {
          this.$swal({
            icon: 'success',
            title: 'Importação iniciada',
            text: 'A importação ocorrerá em segundo plano. Você pode acompanhar o progresso na página de importações.',
            showCancelButton: true,
            confirmButtonText: 'Ir para Importações',
            cancelButtonText: 'Fechar',
            customClass: {
              confirmButton: 'btn btn-success',
              cancelButton: 'btn btn-secondary',
            },
            buttonsStyling: false,
          }).then(result => {
            // SweetAlert2 returns an object with isConfirmed when confirmed
            if (result && result.isConfirmed) {
              // navigate to the importacoes route
              try {
                this.$router.push({ name: 'importacao-registros' })
              } catch (e) {
                // fallback: change path directly if named route missing
                this.$router.push('/importacao-registros')
              }
            }
          })
        }
      } catch (err) {
        this.error = (err && err.response && err.response.data && err.response.data.message) || err.message || 'Erro na importação.'
      } finally {
        this.importing = false
      }
    },
    reset() {
      this.currentStep = 1
      this.selectedTipoEntidade = this.tipoEntidade || null
      this.file = null
      this.fileState = null
      this.error = ''
      this.uploading = false
      this.uploadProgress = 0
      this.serverHeaders = []
      this.resourceFields = []
      this.mappings = {}
      this.mappingErrors = {}
      this.mappingAuto = {}
      this.importacaoId = null
      this.uploadedFileName = null
      this.serverRowCount = null
      this.importing = false
      // reset local expected format (do not mutate prop)
      this.expectedFormatToShow = Array.isArray(this.expectedFormat) ? this.expectedFormat.map(f => ({
        name: f.name,
        label: f.label,
        description: f.description,
        type: f.type,
        typeLabel: this.translateType(f.type),
      })) : []
    },
  },
}
</script>

<style scoped>
.w-100 { width: 100%; }
/* Scrollable container for expected format list */
.expected-format-scroll {
  max-height: 200px; /* limita o tamanho do modal quando há muitos campos */
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #fff;
}
.expected-format-scroll.expanded {
  max-height: none; /* remove limite quando o usuário escolhe ver tudo */
}
/* Webkit scrollbar styling para tornar o scroll mais visível */
.expected-format-scroll::-webkit-scrollbar {
  width: 10px;
}
.expected-format-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
.expected-format-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}
/* Firefox scrollbar */
.expected-format-scroll {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}
</style>
